import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Check, Loader2, Eye, EyeOff, Tag, CheckCircle2, Gift } from 'lucide-react';
import { getPlanById, BILLING_PERIODS } from '@/config/pricingPlans';
import { trackCheckoutStarted } from '@/utils/analytics';
import { useTheme } from '@/contexts/ThemeContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutPage = ({ user, onLogin }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState(null);
  
  // Auth state for new users
  const [isNewUser, setIsNewUser] = useState(!user);
  const [authMode, setAuthMode] = useState('signup'); // 'signup' or 'login'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: ''
  });
  
  const planId = searchParams.get('plan');
  const billingParam = searchParams.get('billing') || BILLING_PERIODS.MONTHLY;
  const [billingPeriod, setBillingPeriod] = useState(billingParam);
  
  const plan = getPlanById(planId);
  const { isDarkMode } = useTheme();
  
  // Dark mode color scheme
  const colors = isDarkMode ? {
    background: '#092036',
    cardBg: '#0f2744',
    heading: '#ffffff',
    bodyText: '#e2e8f0',
    subtext: '#9ca3af',
    border: '#1e3a5f',
    inputBg: '#0a1628',
    selectedBg: '#1e3a5f',
    selectedBorder: '#60a5fa'
  } : {
    background: '#F4F4F4',
    cardBg: '#ffffff',
    heading: '#092036',
    bodyText: '#4B5563',
    subtext: '#70767C',
    border: '#e5e7eb',
    inputBg: '#ffffff',
    selectedBg: '#eff6ff',
    selectedBorder: '#3b82f6'
  };

  useEffect(() => {
    if (!plan) {
      navigate('/');
    }
  }, [plan, navigate]);

  useEffect(() => {
    setIsNewUser(!user);
  }, [user]);

  if (!plan) {
    return null;
  }

  const IconComponent = plan.icon;

  const getDisplayPrice = () => {
    if (plan.pricing.type === 'per-case') {
      return {
        amount: plan.pricing.amount,
        formatted: `$${plan.pricing.amount}`,
        label: '/case',
        savings: null
      };
    }
    if (plan.pricing.type === 'annual-only') {
      return {
        amount: plan.pricing.amount,
        formatted: `$${plan.pricing.amount}`,
        label: '/yr',
        savings: null
      };
    }
    if (billingPeriod === BILLING_PERIODS.ANNUAL) {
      return {
        amount: plan.pricing.annualAmount,
        formatted: `$${plan.pricing.annualAmount.toLocaleString()}`,
        label: '/yr',
        savings: plan.pricing.annualSavings
      };
    }
    return {
      amount: plan.pricing.monthlyAmount,
      formatted: `$${plan.pricing.monthlyAmount}`,
      label: '/mo',
      savings: null
    };
  };

  const priceDisplay = getDisplayPrice();

  // Coupon validation handler
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }
    
    setCouponLoading(true);
    setCouponError(null);
    
    try {
      const sessionToken = localStorage.getItem('session_token');
      
      // If user is not logged in, show a helpful message but don't block
      if (!sessionToken) {
        setCouponError('Please log in or create an account to apply a coupon code');
        setCouponLoading(false);
        return;
      }
      
      const formDataCoupon = new FormData();
      formDataCoupon.append('coupon_code', couponCode.trim());
      formDataCoupon.append('plan_id', plan.id);
      
      const response = await axios.post(`${API}/coupons/validate`, formDataCoupon, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });
      
      setCouponApplied(response.data);
      setCouponError(null);
    } catch (err) {
      setCouponError(err.response?.data?.detail || 'Invalid coupon code');
      setCouponApplied(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCouponApplied(null);
    setCouponCode('');
    setCouponError(null);
  };

  const validateForm = () => {
    if (authMode === 'signup' && (!formData.name || formData.name.trim().length < 2)) {
      setError('Please enter your full name');
      return false;
    }
    if (!formData.email || !formData.email.includes('@')) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!formData.password || formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    if (authMode === 'signup' && formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleAuthAndCheckout = async () => {
    setError(null);
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      let sessionToken;
      let userEmail = formData.email;

      if (authMode === 'signup') {
        // Create new account
        const signupData = new FormData();
        signupData.append('email', formData.email);
        signupData.append('password', formData.password);
        signupData.append('name', formData.name.trim());
        
        const signupResponse = await axios.post(`${API}/auth/register`, signupData);
        sessionToken = signupResponse.data.session_token;
        
        // Store token
        localStorage.setItem('session_token', sessionToken);
        
        // Notify parent of login if callback exists
        if (onLogin) {
          onLogin(signupResponse.data.user);
        }
      } else {
        // Login existing user
        const loginData = new FormData();
        loginData.append('email', formData.email);
        loginData.append('password', formData.password);
        
        const loginResponse = await axios.post(`${API}/auth/login`, loginData);
        sessionToken = loginResponse.data.session_token;
        userEmail = loginResponse.data.user.email;
        
        // Store token
        localStorage.setItem('session_token', sessionToken);
        
        // Notify parent of login if callback exists
        if (onLogin) {
          onLogin(loginResponse.data.user);
        }
      }

      // Now proceed to Stripe checkout
      await proceedToStripeCheckout(sessionToken);
      
    } catch (err) {
      console.error('Auth/Checkout error:', err);
      setError(err.response?.data?.detail || 'An error occurred. Please try again.');
      setLoading(false);
    }
  };

  const proceedToStripeCheckout = async (token) => {
    try {
      trackCheckoutStarted(plan.id, billingPeriod, couponApplied ? 0 : priceDisplay.amount);

      const sessionToken = token || localStorage.getItem('session_token');
      
      const response = await axios.post(
        `${API}/subscriptions/checkout`,
        {
          plan_id: plan.id,
          billing_period: billingPeriod,
          success_url: `${window.location.origin}/checkout/success?plan=${plan.id}`,
          cancel_url: `${window.location.origin}/checkout?plan=${plan.id}&billing=${billingPeriod}`,
          coupon_code: couponApplied?.coupon_code || couponCode.trim() || null
        },
        {
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Check if coupon was applied and access was granted directly (no Stripe needed)
      if (response.data.access_granted && response.data.coupon_applied) {
        // Redirect to success page for beta users
        window.location.href = response.data.redirect_url;
        return;
      }

      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.detail || 'Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (!user && isNewUser) {
      await handleAuthAndCheckout();
    } else {
      setLoading(true);
      setError(null);
      await proceedToStripeCheckout();
    }
  };

  return (
    <div className="min-h-screen pb-12 px-6 sm:pt-12" style={{ backgroundColor: colors.background }}>
      <div className="max-w-lg mx-auto">
        {/* Back Link */}
        <button 
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm font-medium mb-8 hover:underline"
          style={{ color: colors.subtext }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <Card className="p-8 shadow-lg" style={{ backgroundColor: colors.cardBg, borderColor: colors.border }}>
          <h1 className="text-2xl font-bold mb-6" style={{ color: colors.heading }}>
            {user ? 'Complete Your Purchase' : 'Create Account & Subscribe'}
          </h1>

          {/* Plan Summary */}
          <div className="flex items-start gap-4 p-4 rounded-lg mb-6" style={{ backgroundColor: isDarkMode ? colors.inputBg : '#f9fafb' }}>
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${plan.badgeColor || plan.color}15` }}
            >
              <IconComponent className="w-6 h-6" style={{ color: plan.badgeColor || plan.color }} />
            </div>
            <div>
              <h2 className="font-semibold" style={{ color: colors.heading }}>
                {plan.name}
              </h2>
              <p className="text-sm" style={{ color: colors.subtext }}>
                {plan.tagline}
              </p>
            </div>
          </div>

          {/* Auth Form for New Users */}
          {!user && (
            <div className="mb-6">
              {/* Auth Mode Toggle */}
              <div className="flex mb-4 rounded-lg p-1" style={{ backgroundColor: isDarkMode ? colors.inputBg : '#f3f4f6' }}>
                <button
                  onClick={() => setAuthMode('signup')}
                  className="flex-1 py-2 text-sm font-medium rounded-md transition-all"
                  style={{
                    backgroundColor: authMode === 'signup' ? colors.cardBg : 'transparent',
                    color: authMode === 'signup' ? colors.heading : colors.subtext,
                    boxShadow: authMode === 'signup' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
                  }}
                >
                  New Account
                </button>
                <button
                  onClick={() => setAuthMode('login')}
                  className="flex-1 py-2 text-sm font-medium rounded-md transition-all"
                  style={{
                    backgroundColor: authMode === 'login' ? colors.cardBg : 'transparent',
                    color: authMode === 'login' ? colors.heading : colors.subtext,
                    boxShadow: authMode === 'login' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
                  }}
                >
                  Existing Account
                </button>
              </div>

              <div className="space-y-4">
                {authMode === 'signup' && (
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium" style={{ color: colors.heading }}>Full Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="Enter your full name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1"
                      style={{ backgroundColor: colors.inputBg, color: colors.heading, borderColor: colors.border }}
                      required
                    />
                  </div>
                )}
                
                <div>
                  <Label htmlFor="email" className="text-sm font-medium" style={{ color: colors.heading }}>Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1"
                    style={{ backgroundColor: colors.inputBg, color: colors.heading, borderColor: colors.border }}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="password" className="text-sm font-medium" style={{ color: colors.heading }}>Password</Label>
                  <div className="relative mt-1">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      style={{ backgroundColor: colors.inputBg, color: colors.heading, borderColor: colors.border }}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                      style={{ color: colors.subtext }}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {authMode === 'signup' && (
                  <div>
                    <Label htmlFor="confirmPassword" className="text-sm font-medium" style={{ color: colors.heading }}>Confirm Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      className="mt-1"
                      style={{ backgroundColor: colors.inputBg, color: colors.heading, borderColor: colors.border }}
                      required
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Billing Toggle - only show for subscription plans */}
          {plan.pricing.type === 'subscription' && (
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2" style={{ color: colors.heading }}>
              Billing Period
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setBillingPeriod(BILLING_PERIODS.MONTHLY)}
                className="p-4 rounded-lg border-2 text-left transition-all"
                style={{
                  borderColor: billingPeriod === BILLING_PERIODS.MONTHLY ? colors.selectedBorder : colors.border,
                  backgroundColor: billingPeriod === BILLING_PERIODS.MONTHLY ? colors.selectedBg : colors.cardBg
                }}
              >
                <div className="font-semibold" style={{ color: colors.heading }}>
                  Monthly
                </div>
                <div className="text-sm" style={{ color: colors.subtext }}>
                  ${plan.pricing.monthlyAmount}/mo
                </div>
              </button>
              <button
                onClick={() => setBillingPeriod(BILLING_PERIODS.ANNUAL)}
                className="p-4 rounded-lg border-2 text-left transition-all relative"
                style={{
                  borderColor: billingPeriod === BILLING_PERIODS.ANNUAL ? colors.selectedBorder : colors.border,
                  backgroundColor: billingPeriod === BILLING_PERIODS.ANNUAL ? colors.selectedBg : colors.cardBg
                }}
              >
                <span className="absolute -top-2 right-2 text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                  Best Value
                </span>
                <div className="font-semibold" style={{ color: colors.heading }}>
                  Annual
                </div>
                <div className="text-sm" style={{ color: colors.subtext }}>
                  ${plan.pricing.annualAmount.toLocaleString()}/yr
                </div>
              </button>
            </div>
          </div>
          )}

          {/* Per-case pricing display */}
          {plan.pricing.type === 'per-case' && (
          <div className="mb-6">
            <div className="p-4 rounded-lg border-2" style={{ borderColor: colors.selectedBorder, backgroundColor: colors.selectedBg }}>
              <div className="font-semibold" style={{ color: colors.heading }}>
                ${plan.pricing.amount} per case
              </div>
            </div>
          </div>
          )}

          {/* Annual-only pricing display */}
          {plan.pricing.type === 'annual-only' && (
          <div className="mb-6">
            <div className="p-4 rounded-lg border-2" style={{ borderColor: colors.selectedBorder, backgroundColor: colors.selectedBg }}>
              <div className="font-semibold" style={{ color: colors.heading }}>
                ${plan.pricing.amount} per year
              </div>
            </div>
          </div>
          )}

          {/* What's Included */}
          <div className="mb-6">
            <h3 className="text-sm font-medium mb-3" style={{ color: colors.heading }}>
              What&apos;s Included
            </h3>
            <ul className="space-y-2">
              {plan.bullets.map((bullet, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: plan.badgeColor || plan.color }} />
                  <span style={{ color: colors.bodyText }}>{bullet}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Coupon Code Section */}
          <div className="mb-6">
            <Label className="text-sm font-medium flex items-center gap-2" style={{ color: colors.heading }}>
              <Tag className="w-4 h-4" />
              Have a coupon code?
            </Label>
            
            {couponApplied ? (
              // Coupon Applied State
              <div className="mt-2 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Gift className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-semibold text-green-800">{couponApplied.coupon_code}</p>
                      <p className="text-sm text-green-600">{couponApplied.description}</p>
                    </div>
                  </div>
                  <button
                    onClick={removeCoupon}
                    className="text-sm text-gray-500 hover:text-gray-700 underline"
                  >
                    Remove
                  </button>
                </div>
                {couponApplied.discount_percent === 100 && (
                  <div className="mt-3 p-3 bg-green-100 rounded-md">
                    <div className="flex items-center gap-2 text-green-800">
                      <CheckCircle2 className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        100% off - No payment required!
                      </span>
                    </div>
                    <p className="text-xs text-green-700 mt-1">
                      Includes All Access Pass + Curbside Consult for {couponApplied.duration_months} months
                    </p>
                  </div>
                )}
              </div>
            ) : (
              // Coupon Input State
              <div className="mt-2">
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    className="flex-1 uppercase"
                    disabled={couponLoading}
                    style={{ backgroundColor: colors.inputBg, color: colors.heading, borderColor: colors.border }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleApplyCoupon}
                    disabled={couponLoading || !couponCode.trim()}
                    className="px-4"
                  >
                    {couponLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Apply'
                    )}
                  </Button>
                </div>
                {couponError && (
                  <p className="text-sm text-red-500 mt-2">{couponError}</p>
                )}
              </div>
            )}
          </div>

          {/* Price Summary */}
          <div className="p-4 rounded-lg mb-6" style={{ backgroundColor: isDarkMode ? colors.inputBg : '#f9fafb' }}>
            <div className="flex items-center justify-between">
              <span style={{ color: colors.subtext }}>Total</span>
              <div className="text-right">
                {couponApplied?.discount_percent === 100 ? (
                  <>
                    <span className="text-2xl font-bold text-green-600">
                      $0
                    </span>
                    <span className="text-sm line-through ml-2" style={{ color: colors.subtext }}>
                      {priceDisplay.formatted}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="text-2xl font-bold" style={{ color: colors.heading }}>
                      {priceDisplay.formatted}
                    </span>
                    <span className="text-sm" style={{ color: colors.subtext }}>
                      {priceDisplay.label}
                    </span>
                  </>
                )}
                {priceDisplay.savings && !couponApplied && (
                  <div className="text-sm font-medium" style={{ color: '#16a34a' }}>
                    {priceDisplay.savings}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Checkout Button */}
          <Button
            onClick={handleCheckout}
            disabled={loading}
            className="w-full py-6 text-base font-semibold rounded-xl text-white"
            style={{ backgroundColor: couponApplied?.discount_percent === 100 ? '#16a34a' : '#FF6B35' }}
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Processing...
              </>
            ) : couponApplied?.discount_percent === 100 ? (
              <>
                {user ? 'Activate Free Access' : (authMode === 'signup' ? 'Create Account & Activate' : 'Sign In & Activate')}
              </>
            ) : (
              <>
                {user ? 'Continue to Payment' : (authMode === 'signup' ? 'Create Account & Pay' : 'Sign In & Pay')}
              </>
            )}
          </Button>

          <p className="text-xs text-center mt-4" style={{ color: colors.subtext }}>
            Secure payment powered by Stripe. Cancel anytime.
          </p>
        </Card>
      </div>
    </div>
  );
};

export default CheckoutPage;
